import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Plus, Briefcase, Trash2, Edit, X, ArrowRightLeft, Eye, Server, ChevronDown } from 'lucide-react'
import { ConfirmModal } from '../components/ConfirmModal'

function ProcessViewModal({ process, documents = [], assets = [], onClose, onEdit }) {
  const parseJSON = (str) => {
    try { return JSON.parse(str || '[]') } catch { return [] }
  }
  const attachedIds = parseJSON(process.evidence_docs)
  const attachedDocs = documents.filter(d => attachedIds.includes(String(d.id)))

  const linkedAssetIds = parseJSON(process.associated_assets).map(Number)
  const linkedAssets = assets.filter(a => linkedAssetIds.includes(a.id))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: 'var(--accent)22', color: 'var(--accent)', padding: 8, borderRadius: 8 }}>
              <Briefcase size={18} />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Process Details</h2>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                PRC-{String(process.id).padStart(3, '0')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline btn-sm" onClick={onEdit}><Edit size={14} /> Edit</button>
            <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <label className="form-label">Process Name</label>
              <p style={{ fontSize: '1.125rem', fontWeight: 600, margin: '4px 0 0 0' }}>{process.process_name}</p>
            </div>
            <div>
              <label className="form-label">Owner</label>
              <p style={{ margin: '4px 0 0 0' }}><span className="badge badge-neutral">{process.owner || 'Unassigned'}</span></p>
            </div>
          </div>

          <div className="grid-2" style={{ marginBottom: '20px' }}>
            <div>
              <label className="form-label">Inputs</label>
              <p className="text-muted" style={{ whiteSpace: 'pre-wrap', background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem' }}>
                {process.inputs || '—'}
              </p>
            </div>
            <div>
              <label className="form-label">Outputs</label>
              <p className="text-muted" style={{ whiteSpace: 'pre-wrap', background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem' }}>
                {process.outputs || '—'}
              </p>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Description</label>
            <p className="text-muted" style={{ whiteSpace: 'pre-wrap', background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem' }}>
              {process.description || 'No description provided.'}
            </p>
          </div>

          <div>
            <label className="form-label">Attached Documents</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {attachedDocs.map(d => (
                <span key={d.id} className="badge badge-accent" style={{ fontSize: '0.75rem', padding: '6px 12px' }}>
                  {d.title}
                </span>
              ))}
              {attachedDocs.length === 0 && <span className="text-muted" style={{ fontSize: '0.875rem' }}>—</span>}
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <label className="form-label">Linked Assets</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {linkedAssets.map(a => (
                <span key={a.id} className="badge badge-neutral" style={{ fontSize: '0.75rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Server size={12} />
                  {a.asset_name}
                </span>
              ))}
              {linkedAssets.length === 0 && <span className="text-muted" style={{ fontSize: '0.875rem' }}>—</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ProcessModal({ process, documents = [], assets = [], onClose, onSave, isSaving }) {
  const parseJSON = (str) => {
    try { return JSON.parse(str || '[]') } catch { return [] }
  }

  const [form, setForm] = useState(() => {
    if (process) {
      return { 
        ...process, 
        evidence_docs: parseJSON(process.evidence_docs),
        associated_assets: parseJSON(process.associated_assets).map(Number)
      }
    }
    return {
      process_name: '', owner: '', description: '', inputs: '', outputs: '', associated_assets: [], evidence_docs: []
    }
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const [assetSearch, setAssetSearch] = useState('')
  const [showAssetResults, setShowAssetResults] = useState(false)

  const toggleAsset = (id) => {
    const aid = Number(id)
    setForm(prev => {
      const exists = prev.associated_assets.includes(aid)
      const next = exists ? prev.associated_assets.filter(x => x !== aid) : [...prev.associated_assets, aid]
      return { ...prev, associated_assets: next }
    })
  }

  const filteredAssets = assets.filter(a =>
    !form.associated_assets.includes(a.id) &&
    a.asset_name.toLowerCase().includes(assetSearch.toLowerCase())
  ).slice(0, 10)

  const toggleDoc = (docId) => {
    setForm(f => {
      const current = f.evidence_docs || []
      const next = current.includes(String(docId))
        ? current.filter(id => id !== String(docId))
        : [...current, String(docId)]
      return { ...f, evidence_docs: next }
    })
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{process ? 'Edit Process' : 'New Process Definition'}</h2>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Process Name *</label>
            <input className="form-input" value={form.process_name} onChange={e => set('process_name', e.target.value)} placeholder="e.g. User Authentication Flow" />
          </div>
          <div className="form-group">
            <label className="form-label">Process Owner</label>
            <input className="form-input" value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="e.g. Product Manager" />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Inputs</label>
              <textarea className="form-textarea" style={{ minHeight: 80 }} value={form.inputs} onChange={e => set('inputs', e.target.value)} placeholder="Data or triggers that start the process..." />
            </div>
            <div className="form-group">
              <label className="form-label">Outputs</label>
              <textarea className="form-textarea" style={{ minHeight: 80 }} value={form.outputs} onChange={e => set('outputs', e.target.value)} placeholder="Results or deliverables of the process..." />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Attached Documents</label>
            <select className="form-select" value="" onChange={e => toggleDoc(e.target.value)}>
              <option value="" disabled>Link a document...</option>
              {documents.map(d => (
                <option key={d.id} value={d.id}>{(form.evidence_docs || []).includes(String(d.id)) ? '✓ ' : ''} {d.title}</option>
              ))}
            </select>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
              {(form.evidence_docs || []).map(id => {
                const d = documents.find(doc => String(doc.id) === String(id))
                return d ? (
                  <span key={id} className="badge badge-accent" style={{ fontSize: '0.6875rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {d.title}
                    <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => toggleDoc(id)} />
                  </span>
                ) : null
              })}
            </div>
          </div>

          {/* LINKED ASSETS */}
          <div className="form-group" style={{ marginTop: 20 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Server size={13} /> Linked Assets
            </label>
            <div className="ctrl-search-container" style={{ position: 'relative' }}>
              <div 
                className="form-input flex items-center justify-between cursor-pointer"
                style={{ background: 'var(--bg-elevated)', minHeight: 42, flexWrap: 'wrap', gap: 6, padding: '6px 12px' }}
                onClick={() => setShowAssetResults(!showAssetResults)}
              >
                {form.associated_assets.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {form.associated_assets.map(aid => {
                      const a = assets.find(x => x.id === aid)
                      return (
                        <span key={aid} className="badge badge-neutral" style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                          <Server size={10} />
                          {a?.asset_name || `ID-${aid}`}
                          <X size={10} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleAsset(aid) }} />
                        </span>
                      )
                    })}
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>Link assets to this process...</span>
                )}
                <ChevronDown size={16} style={{ opacity: 0.5, marginLeft: 'auto' }} />
              </div>

              {showAssetResults && (
                <div className="ctrl-search-results shadow-xl" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 101, background: 'var(--bg-modal)', border: '1px solid var(--border)', borderRadius: 12, marginTop: 8, maxHeight: 250, overflowY: 'auto' }}>
                  <div style={{ padding: 10, borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0, background: 'var(--bg-modal)' }}>
                    <input 
                      autoFocus
                      className="form-input" 
                      placeholder="Search assets..." 
                      value={assetSearch} 
                      onChange={e => setAssetSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ height: 32, fontSize: '0.8125rem' }}
                    />
                  </div>
                  <div style={{ padding: '4px 0' }}>
                    {filteredAssets.length > 0 ? filteredAssets.map(a => (
                      <div 
                        key={a.id} 
                        className="ctrl-search-item" 
                        style={{ padding: '8px 16px', cursor: 'pointer', transition: 'background 0.2s', borderBottom: '1px solid var(--border-light)' }}
                        onClick={(e) => { e.stopPropagation(); toggleAsset(a.id); setAssetSearch(''); setShowAssetResults(false) }}
                      >
                        <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{a.asset_name}</div>
                        {a.asset_type && <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{a.asset_type}</div>}
                      </div>
                    )) : (
                      <div style={{ padding: 15, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No assets found.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Detailed description of process steps..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!form.process_name || isSaving} onClick={() => onSave(form)}>
            {isSaving ? 'Saving…' : process ? 'Update Process' : 'Create Process'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function Processes({ toast }) {
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [sortBy, setSortBy] = useState('id_asc')
  const [confirmDelete, setConfirmDelete] = useState(null)

  const { data: processes = [], isLoading } = useQuery({ queryKey: ['processes'], queryFn: api.getProcesses })
  const { data: documents = [] } = useQuery({ queryKey: ['documents'], queryFn: api.getDocuments })
  const { data: assets = [] } = useQuery({ queryKey: ['assets'], queryFn: api.getAssets })

  const createMut = useMutation({
    mutationFn: api.createProcess,
    onSuccess: () => { qc.invalidateQueries(['processes']); setShowModal(false); toast('Process defined', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateProcess(id, data),
    onSuccess: () => { qc.invalidateQueries(['processes']); setEditing(null); toast('Process updated', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  const deleteMut = useMutation({
    mutationFn: ({ id, reason }) => api.deleteProcess(id, reason),
    onSuccess: () => { qc.invalidateQueries(['processes']); toast('Process moved to bin', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Process Register</h1>
          <p>Inventory of business processes mapped to ISMS scope</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Sort by:</span>
          <select className="form-select" style={{ height: 32, padding: '0 28px 0 8px', fontSize: '0.8125rem', width: 'auto' }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="id_asc">ID (Ascending)</option>
            <option value="id_desc">ID (Descending)</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
          </select>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={15} /> Add Process
          </button>
        </div>
      </div>

      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Total Processes</div>
          <div className="stat-value">{processes.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Process Owners</div>
          <div className="stat-value">{new Set(processes.map(p => p.owner)).size}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Critical Path</div>
          <div className="stat-value">3</div>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Process Name</th>
              <th>Owner</th>
              <th>Inputs</th>
              <th>Outputs</th>
              <th>Attached Documents</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8}><div className="empty-state">Loading…</div></td></tr>
            )}
            {!isLoading && processes.length === 0 && (
              <tr><td colSpan={8}>
                <div className="empty-state" style={{ padding: '48px 0' }}>
                  <Briefcase size={32} />
                  <p>No processes defined yet. Use "Add Process" to start mapping.</p>
                </div>
              </td></tr>
            )}
            {[...processes].sort((a, b) => {
              if (sortBy === 'id_asc') return a.id - b.id
              if (sortBy === 'id_desc') return b.id - a.id
              if (sortBy === 'name_asc') return a.process_name.localeCompare(b.process_name)
              if (sortBy === 'name_desc') return b.process_name.localeCompare(a.process_name)
              return 0
            }).map(p => {
              const parseJSON = (str) => {
                try { return JSON.parse(str || '[]') } catch { return [] }
              }
              const attachedIds = parseJSON(p.evidence_docs)
              const attachedDocs = documents.filter(d => attachedIds.includes(String(d.id)))

              return (
                <tr key={p.id}>
                  <td><span className="font-mono text-xs">PRC-{String(p.id).padStart(3,'0')}</span></td>
                  <td style={{ fontWeight: 500 }}>{p.process_name}</td>
                  <td><span className="badge badge-neutral">{p.owner || 'Unassigned'}</span></td>
                  <td style={{ maxWidth: 200 }} className="truncate text-xs text-muted">{p.inputs || '—'}</td>
                  <td style={{ maxWidth: 200 }} className="truncate text-xs text-muted">{p.outputs || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {attachedDocs.map(d => (
                        <span key={d.id} className="badge badge-accent" style={{ fontSize: '0.75rem', width: 'fit-content' }}>
                          {d.title}
                        </span>
                      ))}
                      {attachedDocs.length === 0 && <span className="text-muted">—</span>}
                    </div>
                  </td>
                  <td className="text-xs text-muted">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={() => setViewing(p)} title="View Details">
                        <Eye size={14} />
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={() => setEditing(p)} title="Edit Process">
                        <Edit size={14} />
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: 6, color: 'var(--danger)' }}
                        onClick={() => setConfirmDelete(p)} title="Delete Process">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {showModal && <ProcessModal documents={documents} assets={assets} onClose={() => setShowModal(false)} onSave={d => createMut.mutate(d)} isSaving={createMut.isPending} />}
      {editing && <ProcessModal process={editing} documents={documents} assets={assets} onClose={() => setEditing(null)} onSave={d => updateMut.mutate({ id: editing.id, data: d })} isSaving={updateMut.isPending} />}
      {viewing && (
        <ProcessViewModal
          process={viewing}
          documents={documents}
          assets={assets}
          onClose={() => setViewing(null)}
          onEdit={() => { setEditing(viewing); setViewing(null); }}
        />
      )}

      <ConfirmModal 
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={(reason) => deleteMut.mutate({ id: confirmDelete.id, reason })}
        title="Move to Bin?"
        message={`Delete process "${confirmDelete?.process_name}"? It will be moved to the Recycle Bin and can be restored later.`}
        showReason={true}
      />
    </div>
  )
}
